/**
 * RemindersTab - Admin Reminder Management Component
 * Extracted from AdminPage.jsx for better code organization
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Settings, Play, Bell, History, BarChart3, 
  Loader2, Trash2, CheckCircle2, XCircle 
} from 'lucide-react';
import { useAdminTranslation } from '../../utils/useAdminTranslation';

const API = process.env.REACT_APP_BACKEND_URL;

export const RemindersTab = () => {
  const { tx } = useAdminTranslation();
  
  // State
  const [reminderConfig, setReminderConfig] = useState({
    scheduler_enabled: true,
    consultando_hours: 48,
    revision_hours: 72,
    consulta_hours: 72,
  });
  const [reminderStatus, setReminderStatus] = useState(null);
  const [reminderHistory, setReminderHistory] = useState([]);
  const [reminderHistoryStats, setReminderHistoryStats] = useState(null);
  const [reminderHistoryFilter, setReminderHistoryFilter] = useState('all');
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [savingReminderConfig, setSavingReminderConfig] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    fetchReminderConfig();
    fetchReminderStatus();
    fetchReminderHistory();
  }, []);

  const fetchReminderConfig = async () => {
    try {
      const res = await axios.get(`${API}/admin/reminder-config`);
      setReminderConfig(res.data);
    } catch (error) {
      console.error('Error fetching reminder config:', error);
    }
  };

  const fetchReminderStatus = async () => {
    try {
      setLoadingReminders(true);
      const res = await axios.get(`${API}/admin/reminder-status`);
      setReminderStatus(res.data);
    } catch (error) {
      console.error('Error fetching reminder status:', error);
    } finally {
      setLoadingReminders(false);
    }
  };

  const fetchReminderHistory = async () => {
    try {
      const res = await axios.get(`${API}/admin/reminder-history`);
      setReminderHistory(res.data.history || []);
      setReminderHistoryStats(res.data.stats || null);
    } catch (error) {
      console.error('Error fetching reminder history:', error);
    }
  };

  // Handlers
  const handleSaveReminderConfig = async () => {
    try {
      setSavingReminderConfig(true);
      await axios.put(`${API}/admin/reminder-config`, reminderConfig);
      toast.success(tx('Configuración guardada', 'Configuration saved'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving config');
    } finally {
      setSavingReminderConfig(false);
    }
  };

  const handleRunReminders = async () => {
    try {
      setLoadingReminders(true);
      const res = await axios.post(`${API}/admin/run-reminders`);
      toast.success(tx(
        `Recordatorios ejecutados: ${res.data.sent_count} enviados`,
        `Reminders executed: ${res.data.sent_count} sent`
      ));
      fetchReminderStatus();
      fetchReminderHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error running reminders');
    } finally {
      setLoadingReminders(false);
    }
  };

  const handleClearHistory = async (days) => {
    if (!window.confirm(tx(
      `¿Eliminar historial de más de ${days} días?`,
      `Delete history older than ${days} days?`
    ))) return;
    try {
      await axios.delete(`${API}/admin/reminder-history?older_than_days=${days}`);
      toast.success(tx('Historial limpiado', 'History cleared'));
      fetchReminderHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error clearing history');
    }
  };

  return (
    <div className="space-y-6">
      {/* Config Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              {tx('Configuración de Recordatorios', 'Reminder Configuration')}
            </CardTitle>
            <CardDescription>
              {tx(
                'Configura los intervalos de tiempo para los recordatorios automáticos',
                'Configure time intervals for automatic reminders'
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={handleRunReminders}
              disabled={loadingReminders}
            >
              {loadingReminders ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
              {tx('Ejecutar Ahora', 'Run Now')}
            </Button>
            <Button 
              onClick={handleSaveReminderConfig}
              disabled={savingReminderConfig}
            >
              {savingReminderConfig ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {tx('Guardar', 'Save')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Scheduler Toggle */}
            <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between">
                <Label className="font-medium">
                  {tx('Scheduler Automático', 'Automatic Scheduler')}
                </Label>
                <Switch
                  checked={reminderConfig.scheduler_enabled}
                  onCheckedChange={(checked) => setReminderConfig({...reminderConfig, scheduler_enabled: checked})}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {reminderConfig.scheduler_enabled 
                  ? tx('✅ Activo - Ejecuta cada hora', '✅ Active - Runs every hour')
                  : tx('⏸️ Pausado', '⏸️ Paused')}
              </p>
              {reminderStatus && (
                <Badge variant={reminderStatus.scheduler_running ? 'default' : 'secondary'} className={reminderStatus.scheduler_running ? 'bg-green-500' : ''}>
                  {reminderStatus.scheduler_running 
                    ? tx('Ejecutándose', 'Running')
                    : tx('Detenido', 'Stopped')}
                </Badge>
              )}
            </div>

            {/* Consultando Hours */}
            <div className="space-y-2">
              <Label>{tx('Pedidos "Consultando"', 'Orders "Consulting"')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  value={reminderConfig.consultando_hours}
                  onChange={(e) => setReminderConfig({...reminderConfig, consultando_hours: parseInt(e.target.value) || 48})}
                  className="w-20"
                />
                <span className="text-sm text-muted-foreground">{tx('horas', 'hours')}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {tx('Aviso al trabajador', 'Notify worker')}
              </p>
            </div>

            {/* Revision Hours */}
            <div className="space-y-2">
              <Label>{tx('Pedidos "Revisión"', 'Orders "Review"')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  value={reminderConfig.revision_hours}
                  onChange={(e) => setReminderConfig({...reminderConfig, revision_hours: parseInt(e.target.value) || 72})}
                  className="w-20"
                />
                <span className="text-sm text-muted-foreground">{tx('horas', 'hours')}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {tx('Recordatorio al cliente', 'Remind customer')}
              </p>
            </div>

            {/* Consulta Hours */}
            <div className="space-y-2">
              <Label>{tx('Consultas Pendientes', 'Pending Queries')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  value={reminderConfig.consulta_hours}
                  onChange={(e) => setReminderConfig({...reminderConfig, consulta_hours: parseInt(e.target.value) || 72})}
                  className="w-20"
                />
                <span className="text-sm text-muted-foreground">{tx('horas', 'hours')}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {tx('Recordatorio al trabajador', 'Remind worker')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            {tx('Estado Actual', 'Current Status')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingReminders ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : reminderStatus ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-amber-800">
                    {tx('Consultando', 'Consulting')}
                  </span>
                  <Badge variant="outline" className="bg-amber-100 text-amber-800">
                    {reminderStatus.consultando_orders_pending_reminder} / {reminderStatus.consultando_orders_total}
                  </Badge>
                </div>
                <p className="text-xs text-amber-600 mt-1">
                  {tx(
                    `${reminderStatus.consultando_orders_pending_reminder} pendientes de recordatorio (>${reminderConfig.consultando_hours}h)`,
                    `${reminderStatus.consultando_orders_pending_reminder} pending reminder (>${reminderConfig.consultando_hours}h)`
                  )}
                </p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-blue-800">
                    {tx('Revisión', 'Review')}
                  </span>
                  <Badge variant="outline" className="bg-blue-100 text-blue-800">
                    {reminderStatus.revision_orders_pending_reminder} / {reminderStatus.revision_orders_total}
                  </Badge>
                </div>
                <p className="text-xs text-blue-600 mt-1">
                  {tx(
                    `${reminderStatus.revision_orders_pending_reminder} pendientes de recordatorio (>${reminderConfig.revision_hours}h)`,
                    `${reminderStatus.revision_orders_pending_reminder} pending reminder (>${reminderConfig.revision_hours}h)`
                  )}
                </p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-purple-800">
                    {tx('Consultas', 'Queries')}
                  </span>
                  <Badge variant="outline" className="bg-purple-100 text-purple-800">
                    {reminderStatus.consultas_pending_reminder} / {reminderStatus.consultas_total}
                  </Badge>
                </div>
                <p className="text-xs text-purple-600 mt-1">
                  {tx(
                    `${reminderStatus.consultas_pending_reminder} pendientes de recordatorio (>${reminderConfig.consulta_hours}h)`,
                    `${reminderStatus.consultas_pending_reminder} pending reminder (>${reminderConfig.consulta_hours}h)`
                  )}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4">
              {tx('No hay datos disponibles', 'No data available')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* History Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              {tx('Historial de Recordatorios', 'Reminder History')}
            </CardTitle>
            {reminderHistoryStats && (
              <CardDescription>
                {tx(
                  `Total: ${reminderHistoryStats.total} | Hoy: ${reminderHistoryStats.today_sent} | Últimos 7 días: ${reminderHistoryStats.last_7_days_sent}`,
                  `Total: ${reminderHistoryStats.total} | Today: ${reminderHistoryStats.today_sent} | Last 7 days: ${reminderHistoryStats.last_7_days_sent}`
                )}
              </CardDescription>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Select value={reminderHistoryFilter} onValueChange={setReminderHistoryFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder={tx('Filtrar por tipo', 'Filter by type')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{tx('Todos', 'All')}</SelectItem>
                <SelectItem value="consultando">Consultando</SelectItem>
                <SelectItem value="revision">{tx('Revisión', 'Review')}</SelectItem>
                <SelectItem value="consulta">{tx('Consulta', 'Query')}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => handleClearHistory(30)}>
              <Trash2 className="h-4 w-4 mr-2" />
              {tx('Limpiar (+30 días)', 'Clear (+30 days)')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingReminders ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : reminderHistory.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{tx('No hay recordatorios enviados', 'No reminders sent')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{tx('Fecha', 'Date')}</TableHead>
                  <TableHead>{tx('Tipo', 'Type')}</TableHead>
                  <TableHead>{tx('Destinatario', 'Recipient')}</TableHead>
                  <TableHead>{tx('Referencia', 'Reference')}</TableHead>
                  <TableHead>{tx('Tiempo', 'Time')}</TableHead>
                  <TableHead>{tx('Estado', 'Status')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reminderHistory
                  .filter(h => !reminderHistoryFilter || reminderHistoryFilter === 'all' || h.reminder_type === reminderHistoryFilter)
                  .map((history) => (
                  <TableRow key={history.id}>
                    <TableCell className="text-sm">
                      {new Date(history.created_at).toLocaleString(tx('es-ES', 'en-US'), {
                        day: '2-digit',
                        month: '2-digit',
                        year: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        history.reminder_type === 'consultando' ? 'bg-amber-100 text-amber-800' :
                        history.reminder_type === 'revision' ? 'bg-blue-100 text-blue-800' :
                        'bg-purple-100 text-purple-800'
                      }>
                        {history.reminder_type === 'consultando' ? 'Consultando' :
                         history.reminder_type === 'revision' ? tx('Revisión', 'Review') :
                         tx('Consulta', 'Query')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="font-medium">{history.recipient_name}</p>
                        <p className="text-muted-foreground text-xs">{history.recipient_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {history.target_type === 'order' ? `#${history.target_number}` : `C#${history.target_number}`}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {history.hours_elapsed}h
                    </TableCell>
                    <TableCell>
                      {history.status === 'sent' ? (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          {tx('Enviado', 'Sent')}
                        </Badge>
                      ) : (
                        <Badge variant="destructive">
                          <XCircle className="h-3 w-3 mr-1" />
                          {tx('Fallido', 'Failed')}
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RemindersTab;
